import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";
const states = {
  open: "Aguardando a clínica",
  waiting_patient: "Aguardando o paciente",
  resolved: "Resolvido",
};
function requestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export default function PatientServiceCenter() {
  const [rows, setRows] = useState(null),
    [selected, setSelected] = useState(null),
    [reply, setReply] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [page, setPage] = useState(1),
    [messagePage, setMessagePage] = useState(1);
  const pending = useRef(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/patient-service", {
        params: { page },
      });
      setRows(data);
      setError("");
    } catch (e) {
      setError(
        e.response?.status === 403
          ? "Seu perfil não tem permissão para atender pacientes."
          : "Não foi possível carregar os atendimentos da unidade.",
      );
    }
  }, [page]);
  useEffect(() => {
    load();
  }, [load]);
  const open = async (id, p = 1) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.get(`/api/patient-service/${id}`, {
        params: { page: p },
      });
      setSelected(data);
      setMessagePage(p);
    } catch (e) {
      setError(
        "Não foi possível abrir o atendimento. Confira sua unidade de trabalho.",
      );
    } finally {
      setBusy(false);
    }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() || busy) return;
    setBusy(true);
    try {
      const op = pending.current || {
        id: selected.thread.id,
        request_id: requestId(),
        message: reply.trim(),
      };
      pending.current = op;
      await axios.post(`/api/patient-service/${op.id}/messages`, {
        request_id: op.request_id,
        message: op.message,
      });
      pending.current = null;
      setReply("");
      await open(selected.thread.id);
      await load();
    } catch (e) {
      if ([401, 403, 404, 409, 422].includes(e.response?.status))
        pending.current = null;
      setError(
        "Não foi possível confirmar o envio. Ao enviar novamente, a última mensagem será repetida com proteção contra duplicidade.",
      );
    } finally {
      setBusy(false);
    }
  };
  const resolve = async () => {
    setBusy(true);
    try {
      await axios.post(`/api/patient-service/${selected.thread.id}/resolve`);
      await open(selected.thread.id);
      await load();
    } catch (e) {
      setError("Não foi possível concluir o atendimento.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>Atendimento aos pacientes</h2>
          <p className="text-muted">
            Conversas da unidade de trabalho selecionada.
          </p>
        </div>
        <Button
          variant="outline-primary"
          disabled={busy}
          onClick={() => {
            load();
            if (selected) open(selected.thread.id, messagePage);
          }}
        >
          Atualizar
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {busy && <Spinner size="sm" />}
      {selected ? (
        <>
          <Button
            className="mb-3"
            variant="link"
            disabled={busy}
            onClick={() => {
              setSelected(null);
              setReply("");
            }}
          >
            ← Voltar à fila
          </Button>
          <Card className="mb-3">
            <Card.Body>
              <Badge>
                {states[selected.thread.status] || selected.thread.status}
              </Badge>
              <h4 className="mt-2">{selected.thread.title}</h4>
              <p>{selected.thread.patient}</p>
              <small>ATD-{selected.thread.id.slice(0, 8).toUpperCase()}</small>
            </Card.Body>
          </Card>
          {[...selected.messages.data].reverse().map((m) => (
            <Card
              key={m.id}
              className="mb-2"
              style={{
                background: m.author_type === "staff" ? "#E5F4F0" : "#fff",
              }}
            >
              <Card.Body>
                <strong>
                  {m.author_type === "staff" ? "Equipe da clínica" : "Paciente"}
                </strong>
                <small className="ms-3">
                  {new Date(m.created_at).toLocaleString("pt-BR")}
                </small>
                <p className="mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {m.body}
                </p>
              </Card.Body>
            </Card>
          ))}
          {selected.messages.last_page > 1 && (
            <div className="d-flex gap-3 my-3">
              <Button
                disabled={busy || messagePage >= selected.messages.last_page}
                onClick={() => open(selected.thread.id, messagePage + 1)}
              >
                Mais antigas
              </Button>
              <span>
                {messagePage}/{selected.messages.last_page}
              </span>
              <Button
                disabled={busy || messagePage <= 1}
                onClick={() => open(selected.thread.id, messagePage - 1)}
              >
                Mais recentes
              </Button>
            </div>
          )}
          <Form onSubmit={send} className="my-3">
            <Form.Label>Resposta ao paciente</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              maxLength={5000}
              required
              value={reply}
              disabled={busy}
              onChange={(e) => setReply(e.target.value)}
            />
            <div className="d-flex gap-2 mt-2">
              <Button type="submit" disabled={busy || !reply.trim()}>
                Enviar resposta
              </Button>
              <Button
                variant="outline-success"
                disabled={busy || selected.thread.status === "resolved"}
                onClick={resolve}
              >
                Marcar como resolvido
              </Button>
            </div>
          </Form>
        </>
      ) : (
        <>
          {rows?.data.map((t) => (
            <Card key={t.id} className="mb-2">
              <Card.Body>
                <div className="d-flex justify-content-between gap-3">
                  <div>
                    <Badge>{states[t.status] || t.status}</Badge>
                    <h5 className="mt-2">{t.title}</h5>
                    <small>
                      {t.patient} · ATD-{t.id.slice(0, 8).toUpperCase()}
                    </small>
                  </div>
                  <Button
                    variant="outline-primary"
                    disabled={busy}
                    onClick={() => open(t.id)}
                  >
                    Abrir atendimento
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
          {rows?.data.length === 0 && (
            <Alert variant="light">
              Nenhuma solicitação de paciente nesta unidade.
            </Alert>
          )}
          {rows && (
            <div className="d-flex gap-3 my-3">
              <Button
                disabled={page <= 1}
                onClick={() => setPage((n) => n - 1)}
              >
                Anterior
              </Button>
              <span>
                {page}/{rows.last_page}
              </span>
              <Button
                disabled={page >= rows.last_page}
                onClick={() => setPage((n) => n + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
